import { useContext } from "react";
import {
  Badge,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";
import { StageContext } from "../../contexes/OrderIdContext";
import { getDlqDateRange, getDlqLogGroup } from "../../helpers/logUtils";
import { getBadgeColour } from "../../helpers/errorUtils";
import EventButtons from "../common/EventButtons";
import { DLQItem } from "../../lusca-search-api/generated-src";

interface DlqTableProps {
  items: DLQItem[];
  sortingColumn: any;
  sortingDescending: boolean;
  onSortingChange: (event: any) => void;
  filteringText: string;
  onFilteringChange: (text: string) => void;
  currentPageIndex: number;
  onPageChange: (pageIndex: number) => void;
  pageSize: number;
  onItemSelect: (item: DLQItem) => void;
  filteredItemsCount: number;
}

export function DlqTable({
  items,
  sortingColumn,
  sortingDescending,
  onSortingChange,
  filteringText,
  onFilteringChange,
  currentPageIndex,
  onPageChange,
  pageSize,
  onItemSelect,
  filteredItemsCount,
}: DlqTableProps) {
  const stage = useContext(StageContext);

  const columnDefinitions = [
    {
      id: "eventsourcekey",
      header: "Queue",
      cell: (item: DLQItem) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Badge color={getBadgeColour(item.eventsourcekey)}>
            {item.eventsourcekey}
          </Badge>
          {item.is_malformed_json && <Badge color="red">Malformed JSON</Badge>}
        </SpaceBetween>
      ),
      sortingField: "eventsourcekey",
    },
    {
      id: "pk",
      header: "DLQ Table PK",
      cell: (item: DLQItem) => item.pk,
      sortingField: "pk",
    },
    {
      id: "timestamp",
      header: "Timestamp",
      cell: (item: DLQItem) => item.timestamp,
      sortingField: "timestamp",
    },
    {
      id: "rawJson",
      header: "Raw JSON",
      cell: (item: DLQItem) => (
        <Button onClick={() => onItemSelect(item)}>
          {item.is_malformed_json ? "View Raw Message" : "View Raw JSON"}
        </Button>
      ),
    },
    {
      id: "logs",
      header: "Logs",
      cell: (item: DLQItem) => {
        const logGroup = getDlqLogGroup(item.eventsourcekey, stage);
        const [startTime, endTime] = getDlqDateRange(item.timestamp);
        return (
          <EventButtons
            identifier={item.pk}
            stage={stage}
            logGroups={[logGroup]}
            dateRange={[startTime, endTime]}
            isDlq
          />
        );
      },
    },
  ];

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={items}
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={onSortingChange}
      filter={
        <TextFilter
          filteringText={filteringText}
          filteringPlaceholder="Search DLQ items"
          filteringAriaLabel="Filter DLQ items"
          onChange={({ detail }) => onFilteringChange(detail.filteringText)}
        />
      }
      header={<Header counter={`(${filteredItemsCount})`}>DLQ Items</Header>}
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
          pagesCount={Math.ceil(filteredItemsCount / pageSize)}
        />
      }
    />
  );
}
